import React, { useState, useEffect } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import aiServiceRecommendations from '../../utils/aiServiceRecommendations';
// import serviceManagement from '../../utils/serviceManagement'; // Removed for ServiceReviewAI focus

const ServiceSetup = ({ data, businessSetup, onSave, onSkip, onBack, isLoading }) => {
  const { currentOrganization, updateOrganization } = useOrganization();
  const [formData, setFormData] = useState({
    selectedServices: [],
    customServiceType: '',
    ...data
  });

  const [showCustomService, setShowCustomService] = useState(false);
  const [recommendedServices, setRecommendedServices] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [savingServices, setSavingServices] = useState(false);

  // Load recommended services based on business type
  useEffect(() => {
    const loadServiceRecommendations = async () => {
      // Get business type from businessSetup data or organization context
      let businessType = null;
      
      if (businessSetup?.businessType) {
        businessType = businessSetup.businessType === 'other' 
          ? businessSetup.customBusinessType 
          : businessSetup.businessType;
      } else if (currentOrganization?.business_type) {
        businessType = currentOrganization.business_type;
      }
      
      if (!businessType) return;

      setLoadingRecommendations(true);
      
      try {
        const businessTypeKey = businessType.toLowerCase();
        
        // Check if it's a standard business type
        const standardServices = aiServiceRecommendations.getRecommendedServices(businessTypeKey);
        
        if (standardServices.length > 0) {
          // Use pre-defined services for standard business types
          setRecommendedServices(standardServices);
          setAiGenerated(false);
        } else {
          // Use AI for custom business types
          const aiResponse = await aiServiceRecommendations.generateServicesForBusinessType(businessTypeKey);
          
          if (aiResponse.success) {
            setRecommendedServices(aiResponse.data);
            setAiGenerated(!aiResponse.fallback);
          }
        }
      } catch (error) {
        console.error('Error loading service recommendations:', error);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    loadServiceRecommendations();
  }, [businessSetup?.businessType, businessSetup?.customBusinessType, currentOrganization?.business_type]);

  const handleServiceToggle = async (service, isRecommended = false) => {
    const serviceId = service.id || service.name;
    const isSelected = formData.selectedServices.some(s => (s.id || s.name) === serviceId);
    
    if (isSelected) {
      // Remove service
      setFormData(prev => ({
        ...prev,
        selectedServices: prev.selectedServices.filter(s => (s.id || s.name) !== serviceId)
      }));
    } else {
      // Add service
      setFormData(prev => ({
        ...prev,
        selectedServices: [...prev.selectedServices, service]
      }));
      
      // Track usage for AI-generated services
      if (isRecommended && service.id && aiGenerated) {
        try {
          await aiServiceRecommendations.incrementServiceUsage(service.id);
        } catch (error) {
          console.error('Error tracking service usage:', error);
        }
      }
    }
  };

  const handleAddCustomService = () => {
    if (formData.customServiceType.trim()) {
      const customService = {
        name: formData.customServiceType.trim(),
        description: 'Custom service',
        custom: true
      };
      
      setFormData(prev => ({
        ...prev,
        selectedServices: [...prev.selectedServices, customService],
        customServiceType: ''
      }));
      setShowCustomService(false);
    }
  };



  const handleSave = async () => {
    try {
      setSavingServices(true);
      
      // ACTION 1: Service saving skipped in ServiceReviewAI focus
      // Selected services are saved to onboarding data but not to primary_services table
      if (formData.selectedServices.length > 0) {
        console.log('Service selection saved to onboarding data:', formData.selectedServices);
        // Service management functionality removed for ServiceReviewAI focus
      }
      
      // ACTION 2: Prepares data to be saved to 'onboarding_data'
      const saveData = {
        ...formData,
        // Convert services to a simple format for storage
        services: formData.selectedServices.map(service => ({
          name: service.name,
          description: service.description,
          custom: service.custom || false
        })),
        servicesSavedToDb: true // Flag to indicate services were saved to database
      };
      
      onSave(saveData); // This passes the data up to be saved in `onboarding_data`
    } catch (error) {
      console.error('Error saving service setup:', error);
    } finally {
      setSavingServices(false);
    }
  };

  const isServiceSelected = (service) => {
    const serviceId = service.id || service.name;
    return formData.selectedServices.some(s => (s.id || s.name) === serviceId);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Wrench" size={24} className="text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">
          What services do you offer?
        </h2>
        <p className="text-text-secondary">
          Select the services you provide to help us set up your service catalog.
        </p>
      </div>

      {/* Recommended Services */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-text-primary">
            Recommended Services
          </label>
          {aiGenerated && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
              <Icon name="Sparkles" size={12} className="mr-1" />
              AI Generated
            </span>
          )}
        </div>

        {loadingRecommendations ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mr-3"></div>
            <span className="text-text-secondary">Loading service recommendations...</span>
          </div>
        ) : (
          <div className="space-y-3">
            {recommendedServices.map((service, index) => {
              const isSelected = isServiceSelected(service);
              return (
                <div
                  key={service.id || index}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    isSelected 
                      ? 'border-primary bg-primary-50' 
                      : 'border-border hover:border-primary-200 hover:bg-surface-hover'
                  }`}
                  onClick={() => handleServiceToggle(service, true)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleServiceToggle(service, true)}
                        className="text-primary focus:ring-primary"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-text-primary">
                        {service.name}
                      </h4>
                      {service.description && (
                        <p className="text-xs text-text-secondary mt-1">
                          {service.description}
                        </p>
                      )}
                      {service.usageCount && service.usageCount > 1 && (
                        <span className="inline-block mt-2 text-xs text-text-muted">
                          Selected by {service.usageCount} other businesses
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Custom Service Input */}
        {showCustomService ? (
          <div className="mt-4 flex space-x-2">
            <Input
              value={formData.customServiceType}
              onChange={(e) => setFormData(prev => ({ ...prev, customServiceType: e.target.value }))}
              placeholder="Enter custom service type"
              className="flex-1"
            />
            <Button
              variant="secondary"
              onClick={handleAddCustomService}
              disabled={!formData.customServiceType.trim()}
            >
              Add
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowCustomService(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            onClick={() => setShowCustomService(true)}
            className="mt-4"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Custom Service
          </Button>
        )}
      </div>



      {/* Selected Services Summary */}
      {formData.selectedServices.length > 0 && (
        <div className="bg-primary-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-text-primary mb-2">
            Selected Services ({formData.selectedServices.length})
          </h3>
          <p className="text-xs text-text-secondary mb-3">
            These services will be added to your business catalog and can be used when creating jobs or managing customers. Click the X to remove a service.
          </p>
          <div className="flex flex-wrap gap-2">
            {formData.selectedServices.map((service, index) => (
              <span
                key={service.id || service.name || index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary text-white group hover:bg-primary-600 transition-colors"
              >
                {service.name}
                {service.custom && (
                  <Icon name="Star" size={10} className="ml-1" />
                )}
                <button
                  onClick={() => handleServiceToggle(service)}
                  className="ml-1 hover:bg-primary-700 rounded-full p-0.5 transition-colors"
                  title="Remove service"
                >
                  <Icon name="X" size={10} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-6">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={isLoading}
        >
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Go Back
        </Button>
        <Button
          variant="ghost"
          onClick={onSkip}
          disabled={isLoading}
        >
          Skip for now
        </Button>
        <Button
          onClick={handleSave}
          disabled={isLoading || savingServices}
          className="px-8"
        >
          {isLoading || savingServices ? 'Saving Services...' : 'Continue'}
          <Icon name="ArrowRight" size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default ServiceSetup; 